import { AppMenuPoint, AppMenuPoints, RedirectService } from '@agilox/common';
import { NavColor, NavColors, NavTranslations } from '@agilox/ui-common';
import { Component, computed, HostListener, inject, input, model, signal } from '@angular/core';

@Component({
	selector: 'ui-app-nav',
	templateUrl: './app-nav.component.html',
	standalone: false,
})
export class AppNavComponent {
	apps = input(AppMenuPoints, {
		transform: (apps: AppMenuPoint[]) => {
			return this.setUrls(apps);
		},
	});

	navTranslations = input<NavTranslations | undefined>();

	myApp = computed(() => {
		return this.apps().find((app: AppMenuPoint) => app.name === 'my') ?? AppMenuPoints[0];
	});

	_apps = computed(() => {
		return this.apps().filter((app: AppMenuPoint) => app.name !== 'my');
	});

	activeApp = input<AppMenuPoint | undefined>();
	environment = input<string>('live');
	navColor = input<NavColor>(NavColors.Dark);
	open = model<boolean>(false);

	hoveredApp = signal<AppMenuPoint | undefined>(undefined);

	private redirectService: RedirectService = inject(RedirectService);

	toggle($event: MouseEvent | PointerEvent) {
		$event.stopPropagation();
		this.open.set(!this.open());
	}

	@HostListener('window:click', ['$event'])
	@HostListener('window:keydown.escape', ['$event'])
	clickOutside(event?: MouseEvent | KeyboardEvent) {
		const target = event?.target as HTMLElement;
		const hasUiAppNavClass = target?.closest('#app-nav');
		if (!hasUiAppNavClass && this.open()) {
			this.open.set(false);
		}
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		// Only handle keyboard navigation if focus is within the app nav
		const target = event.target as HTMLElement;
		if (!target.closest('#app-nav')) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.navigateDown();
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.navigateUp();
				break;
			case 'Enter':
			case ' ':
				// Let the browser handle Enter/Space for buttons and links
				break;
			case 'Escape':
				event.preventDefault();
				if (this.open()) {
					this.open.set(false);
					// Return focus to the app nav button
					setTimeout(() => {
						const appNavButton = document.querySelector('#app-nav button') as HTMLElement;
						appNavButton?.focus();
					});
				}
				break;
		}
	}

	private navigateDown() {
		if (!this.open()) {
			this.open.set(true);
			// Focus first item when menu opens
			setTimeout(() => {
				const firstLink = document.querySelector('#app-nav a') as HTMLElement;
				firstLink?.focus();
			});
			return;
		}

		// Navigate within opened menu
		const focusableElements = document.querySelectorAll('#app-nav a');
		const currentIndex = Array.from(focusableElements).findIndex(
			(el) => el === document.activeElement
		);
		const nextIndex = Math.min(currentIndex + 1, focusableElements.length - 1);
		(focusableElements[nextIndex] as HTMLElement)?.focus();
	}

	private navigateUp() {
		if (!this.open()) {
			return;
		}

		// Navigate within opened menu
		const focusableElements = document.querySelectorAll('#app-nav a');
		const currentIndex = Array.from(focusableElements).findIndex(
			(el) => el === document.activeElement
		);

		if (currentIndex === 0) {
			// Return focus to app nav button
			const appNavButton = document.querySelector('#app-nav button') as HTMLElement;
			appNavButton?.focus();
		} else {
			const prevIndex = Math.max(currentIndex - 1, 0);
			(focusableElements[prevIndex] as HTMLElement)?.focus();
		}
	}

	setHoveredApp(app: AppMenuPoint | undefined) {
		this.hoveredApp.set(app);
	}

	private setUrls(apps: AppMenuPoint[]) {
		return apps.map((app: AppMenuPoint) => {
			app.url = this.redirectService.getRedirectAppUrl(app.name, this.environment());
			return app;
		});
	}
}
